import { ISlot } from '../../organisms/bo_calendar_slot/types';
import { HasStartAndEndDates } from '@bondsports/types';
import { MIDNIGHT } from '../../lib/timeUtils';
import { DateTimeFormats } from '@bondsports/date-time';
import dayjs from 'dayjs';
import { getEffectiveEndTime, getEffectiveStartTime } from './effectiveSlotTimes';

/**
 * Returns the slot start and end dates based on the date
 * @param slot {ISlot} - slot to check
 * @param date {string} - the date to check
 * @returns {HasStartAndEndDates} - start and end dates
 */
const getCalculatedDateTime = (slot: ISlot, date: string): HasStartAndEndDates => {
	const isStartDateBefore: boolean = slot.startDate < date;

	const start: dayjs.Dayjs = dayjs(date);
	const [startHours, startMinutes] = (isStartDateBefore ? MIDNIGHT : getEffectiveStartTime(slot))
		.split(':')
		.map(Number);

	const startDate: string = start
		.set('hour', startHours)
		.set('minute', startMinutes)
		.format(DateTimeFormats.YYYY_MM_DD_T_HH_MM_SS);

	const isEndDateAfter: boolean = slot.endDate > date;

	const end: dayjs.Dayjs = isEndDateAfter ? dayjs(date).add(1, 'day') : dayjs(date);
	const [endHours, endMinutes] = (isEndDateAfter ? MIDNIGHT : getEffectiveEndTime(slot)).split(':').map(Number);

	const endDate: string = end
		.set('hour', endHours)
		.set('minute', endMinutes)
		.format(DateTimeFormats.YYYY_MM_DD_T_HH_MM_SS);

	return { startDate, endDate };
};

/**
 * Check if two slots are overlapping
 * @param slot {ISlot} - slot to check
 * @param other {ISlot} - other slot to check against
 * @param date {string} - date to check
 * @returns {boolean} - true if slots are overlapping, false otherwise
 */
const isOverlapping = (slot: ISlot, other: ISlot, date: string): boolean => {
	const { startDate: slotStart, endDate: slotEnd } = getCalculatedDateTime(slot, date);
	const { startDate: otherStart, endDate: otherEnd } = getCalculatedDateTime(other, date);

	return dayjs(slotStart).isBefore(otherEnd) && dayjs(slotEnd).isAfter(otherStart);
};

const hasCollision = (slot: ISlot, slotsList: ISlot[] = [], date: string) => {
	if (!slot) {
		return true;
	}

	if (slotsList?.length === 0 || !Array.isArray(slotsList)) {
		return false;
	}

	const other = slotsList.find(item => slot.id !== item.id && isOverlapping(slot, item, date));

	return Boolean(other);
};

/**
 * Split slots into groups based on collision
 * @param slots {ISlot[]} - all the slots to split
 * @param date {string} - date to check
 * @returns {ISlot[][]} - groups of slots
 */
export const splitIntoGroups = (slots: ISlot[], date: string): ISlot[][] => {
	const groups: ISlot[][] =
		slots?.map(slot => [slot, ...slots.filter((other: ISlot) => hasCollision(slot, [other], date))]) || [];

	return mergeGroups(groups);
};

/**
 * Merge groups with collision
 * if two groups have at least one common slot, merge them
 */
const mergeGroups = (groups: ISlot[][]): ISlot[][] => {
	const mergedGroups: ISlot[][] = groups.map(group => [...group]);

	for (let index = 0; index < mergedGroups.length; index++) {
		for (let nextIndex = index + 1; nextIndex < mergedGroups.length; nextIndex++) {
			if (mergedGroups[index]?.length && mergedGroups[nextIndex]?.length) {
				const hasOverlap: boolean = mergedGroups[index].some(slot =>
					mergedGroups[nextIndex].some(other => other.id === slot.id)
				);

				if (hasOverlap) {
					mergedGroups[index] = Array.from(new Set([...mergedGroups[index], ...mergedGroups[nextIndex]]));
					mergedGroups[nextIndex] = [];
				}
			}
		}
	}

	return mergedGroups.filter(group => !!group.length);
};

/** Making groups inside group of slots with collision */
export const getSubGroups = (slots: ISlot[], date: string) => {
	const groups = [] as ISlot[][];

	for (const slot of slots) {
		let added: boolean = false;

		for (let i = 0; i < groups.length; i++) {
			if (!hasCollision(slot, groups[i], date)) {
				groups[i].push(slot);
				added = true;
				break;
			}
		}

		if (!added) {
			groups.push([slot]);
		}
	}

	for (let i = 0; i < groups.length; i++) {
		// The slots id may be an uuid or a number, so we need to convert it to string and use localeCompare to sort
		groups[i] = groups[i].sort((a, b) => String(a.id).localeCompare(String(b.id)));
	}

	// The slots id may be an uuid or a number, so we need to convert it to string and use localeCompare to sort
	return groups.sort((a, b) => String(a[0].id).localeCompare(String(b[0].id)));
};
